import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import TooltipForReceptionist from 'components/TooltipForReceptionist';
import IN from 'constants/in';
import USER_ROLE from 'constants/role';
import { useCustomPermission } from 'providers/Access.provider';
import { selectCurrentSalonBilling } from 'selectors/currentSalon.selectors';
import selectEffectiveUserRole from 'selectors/selectEffectiveUserRole';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';

import ListItem from 'settings/page-elements/dropdown/ListItem';
import Switcher from 'settings/page-elements/Switcher';

import CN from './DropdownList.module.scss';

const hasAnyPatchTest = (items) => items.some((service) => service.patch_test);

export const DropdownList = ({ items, listName, showSwitcher, showDescription, childItemCheckPropName, generalName, variant, permission, onCheckboxClick, hideReceptionistBanner = false }) => {
  const hasBilling = useSelector(selectCurrentSalonBilling);
  const userRole = useSelector(selectEffectiveUserRole);
  const hasCustomPermission = useCustomPermission(permission);
  const grantEditAccess = Boolean(permission && hasCustomPermission);

  const [isChecked, setIsChecked] = useState(() => hasAnyPatchTest(items));
  const [patchToggleTime, setPatchToggleTime] = useState(0);
  const [patchSwitchTime, setPatchSwitchTime] = useState(0);

  const isReceptionistBlocked = !hideReceptionistBanner && userRole === USER_ROLE.receptionist && !grantEditAccess;

  const handleCheckboxClick = (name, id, value) => {
    const seconds = new Date().getSeconds();
    if (Math.abs(patchToggleTime - seconds) < 1) return;
    setPatchToggleTime(seconds);
    onCheckboxClick(name, id, value);
    setIsChecked(hasAnyPatchTest(items));
  };

  const handleSwitcherClick = () => {
    const seconds = new Date().getSeconds();
    if (Math.abs(patchSwitchTime - seconds) < 1) return;
    setPatchSwitchTime(seconds);
    items.forEach((item, index) => {
      onCheckboxClick(item.name, item.id, !isChecked, [items.length, index + 1]);
    });
    setIsChecked((prev) => !prev);
  };

  const listItemsClasses = cn({
    [CN[`${variant}Variant`]]: variant,
  });

  return (
    <div data-bem="DropdownList" data-testid="DropdownList" className={CN.component}>
      <div className={CN.listHeading}>
        <span className={CN.title}>{listName}</span>
        {showSwitcher && (
          <div className={CN.relative}>
            <Switcher isChecked={isChecked} checkId={listName} onCheckboxClick={!hasBilling ? alwaysNoop : handleSwitcherClick} />
            {!hideReceptionistBanner && <TooltipForReceptionist permission={permission} where={IN.serviceSwitcher} />}
          </div>
        )}
      </div>
      <div className={listItemsClasses}>
        {items.map((item) => (
          <div key={item.id} data-testid="DropdownList__item" className={CN.item}>
            <ListItem
              itemName={item.name}
              id={item.id.toString()}
              description={showDescription && item.description}
              checked={item[childItemCheckPropName]}
              onCheckboxClick={isReceptionistBlocked ? alwaysNoop : handleCheckboxClick}
              generalName={generalName}
            />
            {!hideReceptionistBanner && <TooltipForReceptionist permission={permission} where={IN.serviceItem} />}
          </div>
        ))}
      </div>
    </div>
  );
};
